/**
 * The Evidence post-upgrade flow's analytics vocabulary: the `action` tokens its
 * two surfaces carry, the payload shapes they report, and the emit seams the app
 * binds.
 *
 * TWO SURFACES, ONE FILE. The welcome popup's "See what's new" is what opens the
 * onboarding modal, so the two are one funnel and their tokens are read together
 * whenever it is analysed. They keep separate token objects because they are
 * separate warehouse rows with separate `screen_name`s — what they share is the
 * file, not a vocabulary.
 *
 * NEITHER SURFACE REPORTS A VIEW, measured rather than assumed: both emit
 * `button_clicked` only. The welcome popup's impression is already covered by
 * the modal queue's own `mark modal as viewed` write, and the onboarding modal
 * reports its step position on every click instead — which is what the funnel
 * reads. No `modal_viewed` seam is invented here to make the pair look
 * symmetrical.
 */
import type {
  TRIALS_AND_UPGRADES_SCREEN,
  TrialPlanContext,
} from './trials-and-upgrades-analytics.types';

/** The `action` tokens the onboarding modal's five CTAs carry. */
export const EVIDENCE_POST_UPGRADE_ONBOARDING_ACTION = {
  next: 'evidence_post_upgrade_onboarding_next',
  gotIt: 'evidence_post_upgrade_onboarding_got_it',
  tryNow: 'evidence_post_upgrade_onboarding_try_now',
  viewGuide: 'evidence_post_upgrade_onboarding_view_guide',
  learnMore: 'evidence_post_upgrade_onboarding_learn_more',
} as const;

export type EvidencePostUpgradeOnboardingActionToken =
  (typeof EVIDENCE_POST_UPGRADE_ONBOARDING_ACTION)[keyof typeof EVIDENCE_POST_UPGRADE_ONBOARDING_ACTION];

/** The `action` tokens the welcome popup's two CTAs carry. */
export const EVIDENCE_POST_UPGRADE_WELCOME_ACTION = {
  seeWhatsNew: 'evidence_post_upgrade_welcome_see_whats_new',
  close: 'evidence_post_upgrade_welcome_close',
} as const;

export type EvidencePostUpgradeWelcomeActionToken =
  (typeof EVIDENCE_POST_UPGRADE_WELCOME_ACTION)[keyof typeof EVIDENCE_POST_UPGRADE_WELCOME_ACTION];

/**
 * `button_clicked` on any of the onboarding modal's five CTAs.
 *
 * `step_number` is 1-based and `total_steps` is the walkthrough's length, both
 * computed by the surface that owns the step machine. They are on every click
 * including the two help-centre links, because the funnel question is which step
 * a user left from.
 */
export type EvidencePostUpgradeOnboardingClickProps = TrialPlanContext & {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['evidencePostUpgradeOnboarding'];
  action: EvidencePostUpgradeOnboardingActionToken;
  label: string;
  step_number: number;
  total_steps: number;
};

/** `button_clicked` on the welcome popup's dismiss or "See what's new". */
export type EvidencePostUpgradeWelcomeClickProps = TrialPlanContext & {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['evidencePostUpgradeWelcome'];
  action: EvidencePostUpgradeWelcomeActionToken;
  label: string;
};

/**
 * The emit seam for an onboarding-modal CTA.
 *
 * A single seam over the `action` union rather than five: the surface already
 * funnels all five through one `trackButton(action, label)` helper, and every
 * one carries the identical payload shape.
 */
export type TrackEvidencePostUpgradeOnboardingClick = (
  props: EvidencePostUpgradeOnboardingClickProps
) => void;

/** The emit seam for a welcome-popup CTA. */
export type TrackEvidencePostUpgradeWelcomeClick = (
  props: EvidencePostUpgradeWelcomeClickProps
) => void;
